<template>
  <div>
    <el-row type="flex" justify="space-between" class="title-bar">
      <el-col :span="10">
        <el-button size="small" type="primary" @click="onRealtimeButton" class="mr-r2">
          <span v-show="realtimeButtonActive" ><i class="fas fa-pause-circle text-large mr-r4"/>실시간 로그</span>
          <span v-show="!realtimeButtonActive"><i class="fas fa-play-circle text-large mr-r4"/>실시간 로그</span>
        </el-button>
        <el-button size="small" @click="changeIsChecked" :disabled="disableCheck">확인</el-button>
      </el-col>
      <el-col :span="14" align="right">
        <el-popover placement="top" width="400" v-model="showKeywordSearch" @show="showSearchTag()" trigger="click" @hide="hideSearchTag()">
          <el-row type="flex" justify="space-between">
            <el-col :span="7" class="mr-r4">
              <el-select v-model="selectedText.property">
                <el-option v-for="option in selectOptions" :key="option.fieldId" :label="option.fieldName" :value="option.fieldId"></el-option>
              </el-select>
            </el-col>
            <el-col :span="10" class="mr-r4">
              <el-input v-if="selectedText.property !== 'isChecked'" v-model="selectedText.value" class="mr-r4"
                @keyup.enter.native="addKeywordTag()" @change="showSearchTag()"/>
              <el-select v-else v-model="selectedText.value" @change="showSearchTag()" class="mr-r4">
                <el-option label="확인" value="1" />
                <el-option label="미확인" value="0" />
              </el-select>
            </el-col>
            <el-col :span="7">
              <el-button type="primary" @click="addKeywordTag">검색</el-button>
              <el-button @click="hideSearchTag">취소</el-button>
            </el-col>
          </el-row>
          <el-row>
            <span v-show="checkDuplicate" style="color: #E02323; margin-top: 10px; font-size: 0.75rem;">이미 태그에 추가된 검색어입니다.</span>
          </el-row>
          <el-button size="small" slot="reference" class="mr-r2">태그 검색</el-button>
        </el-popover>
        <w-header-search-button @click="setSearchToolVisible"></w-header-search-button>
      </el-col>
    </el-row>
    <hr class="mr-b10">
    <div class="mr-b10">
      <el-tag class="label mr-r4">태그</el-tag>
      <el-tag class="mr-r10" v-for="(tag, index) in searchTags" :key="tag.label" closable @close="deleteSearchTag(index)">
        {{tag.label}}
      </el-tag>
      <!-- end 1217 -->
    </div>
    <el-row style="margin-bottom: 15px;">
      <el-table-wrapper v-loading="loading" :column-default="columnDefault" ref="logTable" :total="total" :data="logAnalysisData" :columns="logAnalysisColumn" :pagination="true" remote
      :show-custom-header="true" :show-search-tool="showSearchTool" @cell-click="getSelection" highlight-current-row @selection-change="onSelectionChange"
      @condition-change="setTableParameter">
        <template slot-scope="scope" slot="is-checked-slot">
          <span v-if="scope.row.isChecked === '1'">확인</span>
          <span v-else-if="scope.row.isChecked === '0'">미확인</span>
          <span v-else>-</span>
        </template>
        <template slot-scope="scope" slot="log-detail">
          <span class="c-pointer" @click="clickEventDetail(scope.row)">
            <i class="far fa-search-plus"/>
          </span>
        </template>
      </el-table-wrapper>
    </el-row>
    <w-log-detail-popup ref="messagePopup" :selected-log="selectedLog"></w-log-detail-popup>
    <el-popover ref="miniPopup" v-model="showAddTag" @blur="closeTagPopup" align="center"
    :style="showAddTag ? { top: `${tagPopupPosition.top}px`, left: `${tagPopupPosition.left}px`, position: 'absolute',
    height: '50px', width: '300px' } : { display: 'none' }">
      <el-row type="flex" justify="space-between">
        <div class="vertical-center-box mr-r8" style="word-break: 'break-all'">{{selectedText.value}}</div>
        <div class="vertical-center-box" align="right">
          <el-button type="primary" @click="addSearchTag" class="mr-r4">검색</el-button>
          <el-button @click="closeTagPopup">취소</el-button>
        </div>
      </el-row>
      <span v-show="checkDuplicate" style="color: #E02323; margin-top: 10px; font-size: 0.75rem;">이미 태그에 추가된 검색어입니다.</span>
    </el-popover>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import forEach from 'lodash/forEach';
import every from 'lodash/every';

import LogAPI from '@/api/log/common';
import { EMPTY_TEXT } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import msgUtils from '@/utils/message';
import { columnDefault } from '@/constants/table';

import WEventlevelCell from '@/components/table/EventlevelCell';
import WDatetimeRangeButton from '@/components/button/DatetimeRangeButton';
import WLogDetailPopup from './popup/LogDetailPopup';

export default {
  name: 'WLogAnalysisTable',
  components: {
    WEventlevelCell,
    WLogDetailPopup,
    WDatetimeRangeButton,
  },
  props: {
    showSearchPopover: {
      type: Boolean,
      default: false,
    },
    selectOptions: {
      typs: Array,
    },
    // activeTab: String,
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    /**
     * 로그분석 그리드의 Column 커스텀 설정
     * @type {Array}
     */
    const logAnalysisColumn = [
      {
        type: 'selection',
      },
      {
        prop: 'isChecked',
        label: '상태',
        width: 60,
        scopedSlot: 'is-checked-slot',
      },
      {
        prop: 'logType',
        label: '로그종류',
        width: 90,
        formatter: this.getStringFormatter,
        scopedSlot: 'log-type-slot',
        searchable: true,
      },
      {
        prop: 'objName',
        label: '관리대상명',
        width: 150,
        searchable: true,
        formatter: this.getStringFormatter,
      },
      {
        prop: 'sourceName',
        label: '소스',
        width: 150,
        formatter: this.getStringFormatter,
        searchable: true,
      },
      {
        prop: 'filterName',
        label: '필터',
        width: 90,
        formatter: this.getStringFormatter,
        searchable: true,
      },
      {
        prop: 'message',
        label: '메시지',
        formatter: this.getStringFormatter,
        searchable: true,
        scopedSlot: 'event-message',
      },
      {
        prop: '@timestamp',
        label: '수집시각',
        width: 150,
        formatter: this.getDateFormatter,
      },
      {
        label: '상세보기',
        width: 70,
        scopedSlot: 'log-detail',
      },
    ];

    return {
      total: 1,
      loading: false,
      logAnalysisData: [], // 그리드에 뿌려질 로그 데이터
      logAnalysisColumn, // 그리드 커스텀 컬럼 설정
      columnDefault,
      showSearchTool: false, // 그리드 내 검색 도구 Flag
      selectedLog: {}, // 현재 선택된 그리드의 Row -> Sliding Popup에 Props로 전달할 변수
      showAddTag: false, // 텍스트 드래그 시 검색어 추가 팝업 Flag
      tagPopupPosition: { // 검색어 추가 팝업의 위치
        top: 0,
        left: 0,
      },
      showKeywordSearch: false, // 필터 버튼 클릭 후 사용자가 직접 검색어를 입력할 경우 Popup Flag
      selectedText: {
        property: 'message',
      }, // 드래그 or 더블클릭으로 선택된 영역의 텍스트 정보(label, property, value)
      searchTags: [], // 그리드 왼쪽 상단 검색용 태그 리스트의 데이터 배열
      checkDuplicate: false, // 태그 검색 중복 확인 Flag
      // API로 전송될 Parameter들
      startDateTime: '',
      endDateTime: '',
      start: 0,
      limit: 50,
      currentPage: 1,
      params: {},
      realtimeButtonActive: true,
      realtimeFunction: null,
      checkedRow: [],
      tableParameter: null,
      realtimeLogTask: 'realtimeLogTask',
    };
  },
  computed: {
    ...mapGetters(['analysisDateParams', 'selectedNode']),
    disableCheck() {
      const isFilter = every(this.checkedRow, (item) => isNil(item.isChecked));
      if (isEmpty(this.checkedRow) || isFilter) {
        return true;
      }
      return false;
    },
  },
  watch: {
    active: {
      handler(to) {
        if (to) {
          this.loadData();
          this.createRealtimeLogTask(this.realtimeButtonActive);
        } else {
          this.removeRealtimeLogTask();
        }
      },
    },
    analysisDateParams: {
      immediate: true,
      handler(value) {
        if (!isEmpty(value)) {
          this.startDateTime = value.startDatetime;
          this.endDateTime = value.endDatetime;
          this.start = 0;
          this.currentPage = 1;
          this.loadData();
        }
        this.$nextTick(() => {
          this.closeTagPopup();
        });
      },
    },
    selectedNode: {
      immediate: true,
      deep: true,
      handler() {
        this.params = {};
        this.start = 0;
        this.currentPage = 1;
        this.searchTags = [];
        this.tableParameter = null;
        this.showSearchTool = false;
        this.$nextTick(() => {
          if (!isEmpty(this.analysisDateParams) && !isEmpty(this.selectedNode) && this.active) {
            this.loadData();
          }
          this.closeTagPopup();
          this.$refs.logTable.clearFilter();
        });
      },
    },
    showSearchPopover: {
      immediate: true,
      handler(value) {
        if (value) {
          this.closeTagPopup();
          this.$emit('change-popover-show', false);
        }
      },
    },
  },
  created() {
    this.createRealtimeLogTask(true);
  },
  beforeDestroy() {
    this.removeRealtimeLogTask();
  },
  methods: {
    createRealtimeLogTask(andStart) {
      this.$taskManager.createTask(this.realtimeLogTask, 30, this.loadData);
      if (andStart) {
        this.$taskManager.startTask(this.realtimeLogTask);
      }
    },
    removeRealtimeLogTask() {
      if (this.$taskManager.existTask(this.realtimeLogTask)) {
        this.$taskManager.removeTask(this.realtimeLogTask);
      }
    },
    /**
     * 테이블의 헤더 검색 조건이 변경되면 실행되는 이벤트 함수
     * @author Minyeong
     * @param  {[type]} params [description]
     */
    setTableParameter(params) {
      this.start = params.start;
      this.limit = params.limit;
      this.tableParameter = params;
      this.loadData();
    },
    /**
     * 태그 검색어 및 헤더 검색어를 queryList 데이터로 가공
     * @author Minyeong
     * @param  {[type]} queryList [description]
     */
    setQueryList(queryList) {
      this.searchTags.forEach((item) => {
        queryList.push({ field: item.property, value: item.value, operator: 'AND' });
      });
      if (!isEmpty(this.tableParameter)) {
        forEach(this.tableParameter.searchParams, (item) => {
          if (!isEmpty(item)) {
            if (item.field === 'isChecked') {
              queryList.push({ field: item.field, value: item.value === '확인' ? '1' : '0', operator: 'AND' });
            } else {
              queryList.push({ field: item.field, value: item.value, operator: 'AND' });
            }
          }
        });
      }
    },
    chceckLogData() {
      return !isEmpty(this.logAnalysisData);
    },
    /**
     * 이벤트 로그 그룹의 파라미터 설정
     * @author leesy918
     */
    setEventParams() {
      const queryList = [];
      let params = {};
      this.setQueryList(queryList);

      params = { // CHECK
        startDateTime: this.analysisDateParams.startDatetime,
        endDateTime: this.analysisDateParams.endDatetime,
        filterId: this.selectedNode.filterId,
        objId: this.selectedNode.objId,
        start: this.start,
        limit: this.limit,
        logType: this.selectedNode.type,
        queryList,
      };
      return params;
    },
    /**
     * 커스텀 필터 Params 데이터 설정(사용자 정의 필터 그룹)
     * @author leesy918
     */
    setFilterParams() {
      let params = {};
      const queryList = [];

      this.setQueryList(queryList);

      params = {
        startDateTime: this.analysisDateParams.startDatetime,
        endDateTime: this.analysisDateParams.endDatetime,
        customFilterId: this.selectedNode.filterId,
        start: this.start,
        limit: this.limit,
        logType: 'USER',
        queryList,
      };
      return params;
    },
    /**
     * API로부터 데이터 로드
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadData() { // 커스텀 필터 데이터 로드
      this.loading = true;
      let params = {};
      if (this.selectedNode.type === 'user' && this.selectedNode.leaf) {
        params = this.setFilterParams();
      } else if (this.selectedNode.type === 'event' && this.selectedNode.leaf) {
        params = this.setEventParams();
      } else { // applog, syslog, snmptrap 데이터 로드
        this.setParams();
        params = this.params;
      }
      LogAPI.getLogAnalysisData(params).then((response) => {
        if (response.data.success) {
          this.logAnalysisData = response.data.data;
          this.total = response.data.total;
          if (this.searchTags.length === 0) {
            this.params = {};
          }
        }
        this.loading = false;
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그', false));
        throw new Error(e);
      });
    },
    /**
     * API로부터 데이터를 로드하기 위해 파라미터 설정(사용자 정의 필터 그룹 및 이벤트 로그 그룹 외)
     * @author Minyeong
     */
    setParams() {
      const queryList = [];

      this.setQueryList(queryList);

      this.params = {
        ...this.params,
        startDateTime: this.startDateTime,
        endDateTime: this.endDateTime,
        start: this.start,
        limit: this.limit,
        queryList,
      };

      if (this.selectedNode.type !== 'root' && !isEmpty(this.selectedNode.type)) {
        if (!this.selectedNode.leaf) {
          this.params.logType = this.selectedNode.type.toUpperCase();
        } else {
          this.params.logType = this.selectedNode.type.toUpperCase();
          this.params.objId = this.selectedNode.objId;
        }
      }
    },
    /**
     * 날짜 데이터의 표기형식 변환
     * @author Minyeong
     * @param  {Object} row    [description]
     * @param  {Object} column [description]
     * @param  {Datetime} value  [description]
     * @return {String}        [description]
     */
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
    getStringFormatter(row, column, value) {
      if (!isNil(value)) {
        return value;
      }
      return EMPTY_TEXT;
    },
    /**
     * 그리드 검색창 활성화&비활성화
     * @author Minyeong
     */
    setSearchToolVisible() {
      this.showSearchTool = !this.showSearchTool;
    },
    /**
     * 로그의 상세보기 버튼 클릭 시 Sliding팝업 활성화
     * @author Minyeong
     * @param  {Object} row [description]
     * @return {[type]}     [description]
     */
    clickEventDetail(row) {
      this.selectedLog = row;
      this.$refs.messagePopup.$refs.slidingPopup.open();
    },
    showSearchTag() {
      // 중복체크 초기화
      this.checkDuplicate = false;
    },
    /**
     * 드래그 or 더블클릭으로 그리드 내부 텍스트 영역 선택 시 태그 검색 팝업 활성화(cell-click 이벤트)
     * @author Minyeong
     * @param  {Object} row    그리드 Row 객체
     * @param  {Object} column 그리드 Column 객체
     * @return {[type]}        [description]
     */
    getSelection(row, column, cell, event) {
      event.stopPropagation();
      // 수집시각은 상단 Datepicker에서 설정할 수 있으므로 검색 제한
      if (column.label === '수집시각' || column.label === '상세보기') {
        return;
      }
      this.checkDuplicate = false; // 이미 검색어로 태그에 추가되었는지 확인하는 Flag
      const selected = window.getSelection(); // 사용자가 선택한 영역 정보
      const selectedText = selected.toString(); // 영역 정보로부터 사용자가 선택한 텍스트 읽어오기

      if (selectedText && selected.anchorNode === selected.extentNode) {
        // 선택된 텍스트가 있을 때 : 태그 추가용 팝업 ON
        // top, left : 사용자가 선택한 영역의 좌표 위치. 태그 검색 팝업의 위치를 잡아주기 위해 설정
        const top = selected.getRangeAt(0).getBoundingClientRect().top;
        const left = selected.getRangeAt(0).getBoundingClientRect().left;
        this.tagPopupPosition.top = top - 170;
        this.tagPopupPosition.left = left - 260;

        // 태그 검색 팝업 활성화
        this.showAddTag = true;

        // 태그에 노출시킬 라벨(컬럼명 : 값)과 컬럼속성명, 값을 저장
        this.selectedText.label = `${column.label} : ${selectedText}`;
        this.selectedText.property = column.property;
        this.selectedText.value = selectedText;
      } else {
        // 없을 때에는 팝업 띄우지 않음
        this.closeTagPopup();
      }
    },
    /**
     * 필터 버튼 클릭 시 나오는 텍스트 입력창 숨기기
     * @author Minyeong
     */
    hideSearchTag() {
      this.selectedText = {
        property: 'message',
      };
      this.searchKeyword = '';
      this.showKeywordSearch = false;
    },
    /**
     * 그리드 내 드래그&더블클릭 텍스트 선택 취소 시 태그 검색 팝업 비활성화
     * @author Minyeong
     * @return {[type]} [description]
     */
    closeTagPopup() {
      this.selectedText = {
        property: 'message',
      }; // 선택된 텍스트 초기화
      this.showAddTag = false;
    },
    addSearchTagFromKeywordMap(message) {
      const index = findIndex(this.searchTags, (item) => item.label.toLowerCase() === message.label.toLowerCase());
      if (index !== -1) {
        this.checkDuplicate = true;
        event.stopPropagation();
        return;
      }
      this.searchTags.push(message);
    },
    /**
     * 그리드 드래그 or 더블클릭시 나오는 태그 검색 팝업에서 검색 클릭 시 현재 선택된 텍스트를 검색 태그에 추가
     * @author Minyeong
     */
    addSearchTag(event) {
      // checkDuplicate : 태그 중복 체크용 변수
      // 태그 배열에 사용자가 검색하려는 태그가 이미 있는 경우 중복 태그 검색 방지
      const index = findIndex(this.searchTags, (item) => item.label.toLowerCase() === this.selectedText.label.toLowerCase());
      if (index !== -1) {
        this.checkDuplicate = true;
        event.stopPropagation();
        return;
      }
      if (isEmpty(this.selectedText.value)) {
        this.$message(msgUtils.getCustomMsgData('검색값을 입력해주세요.'));
        event.stopPropagation();
        return;
      }
      if (this.selectedText.property === 'isChecked') {
        if (this.selectedText.value !== '-') {
          this.selectedText.value = this.selectedText.value === '확인' ? '1' : '0';
        } else {
          this.closeTagPopup();
          return;
        }
      }

      this.searchTags.push(this.selectedText);
      this.closeTagPopup();
      this.loadData();
      this.$emit('change-search-tags', this.searchTags);
    },
    /**
     * 필터 버튼 클릭 후 Input에 검색어를 직접 입력해 검색 태그 추가할 때
     * @author Minyeong
     */
    addKeywordTag(event) {
      const index = findIndex(this.selectOptions, (item) => item.fieldId === this.selectedText.property);
      const label = this.selectOptions[index].fieldName;

      let value = this.selectedText.value;
      if (this.selectedText.property === 'isChecked') {
        value = value === '1' ? '확인' : '미확인';
      }
      this.selectedText.label = `${label} : ${value}`;
      this.selectedText.value = value;
      this.addSearchTag(event);
      if (this.checkDuplicate) {
        return;
      }
      this.hideSearchTag();
    },
    /**
     * 추가된 검색 태그의 X버튼 클릭 시 검색 태그에서 삭제
     * @author Minyeong
     * @param  {Number} index [삭제할 태그의 index]
     * @return {[type]}       [description]
     */
    deleteSearchTag(index) {
      const prop = this.searchTags[index].property;
      if (prop !== 'message') {
        delete this.params[prop];
      }
      this.searchTags.splice(index, 1);
      this.loadData();
      this.$emit('change-search-tags', this.searchTags);
    },
    /**
     * 실시간 로그 버튼 클릭 시 Toggle
     * @author Minyeong
     * @return {[type]} [description]
     */
    onRealtimeButton(event) {
      event.stopPropagation();
      this.realtimeButtonActive = !this.realtimeButtonActive;
      if (this.realtimeButtonActive) {
        // 실시간 로그 ON
        this.$taskManager.startTask(this.realtimeLogTask);
      } else {
        // 실시간 로그 OFF
        this.$taskManager.stopTask(this.realtimeLogTask);
      }
    },
    /**
     * 그리드 체크박스에 체크 시 실행됨
     * @author Minyeong
     * @param  {[type]} selection [description]
     * @return {[type]}           [description]
     */
    onSelectionChange(selection) {
      this.checkedRow = selection;
    },
    /**
     * 로그의 상태를 미확인 > 확인으로 변경
     * @author Minyeong
     * @return {[type]} [description]
     */
    changeIsChecked(event) {
      event.stopPropagation();
      const checkedRow = [];
      this.$confirm(...msgUtils.getBasicConfirmData(msgUtils.action.CHECK)).then(() => {
        this.loading = true;
        this.checkedRow.forEach((item) => {
          if (item.isChecked === '0') {
            checkedRow.push(item);
          }
        });
        if (isEmpty(checkedRow)) {
          this.$message(msgUtils.getCustomMsgData('변경사항이 없습니다.'));
          this.$refs.logTable.$children[0].clearSelection();
          return;
        }
        const params = checkedRow;
        LogAPI.updateLogIsChecked(params).then((response) => {
          if (response.data.success) {
            this.$message(msgUtils.getResultMsgData(msgUtils.action.CHECK, '로그', response.data.success));
            this.loadData();
          } else {
            this.$message(msgUtils.getResultMsgData(msgUtils.action.CHECK, '로그', response.data.success));
            this.loading = false;
          }
        }).catch((e) => {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.CHECK, '로그', false));
          throw new Error(e);
        });
      }).catch(() => {});
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/log/components/LogAnalysisTable.vue